import { memo, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  IOS_LOCK_SCREEN,
  iosNotificationCardHeight,
  layoutIosNotificationBody,
  layoutIosNotificationTitle,
  measureIosText,
  splitIosEmoji,
} from '@/lib/ios-notification/ios-notification-layout';
import { NOTIFICATION_ENTRANCE_TRANSITION } from '@/pages/onboarding/mockup/notification-motion';
import { STUDIO_EXIT_MS } from './korza-studio';
import { AppIcon } from './push-preview';

// SF Pro on Apple devices (`system-ui` is the optical size iOS uses for 13–15pt
// text; `-apple-system` is not, in Chrome). The lines themselves never depend
// on it: they come from the iOS layout rules.
const FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
// CSS weight of iOS's semibold (same advances as CoreText's). Chrome fakes
// extra bold on top of 590 unless font-synthesis is off (30% thicker stems).
const SEMIBOLD = 590;
// Where the browser puts SF's 15pt baseline in an 18pt line box (measured).
const LINE_BOX_BASELINE = 46 / 3;

// On the lock screen a push rises from the bottom; one that goes away just
// fades where it is (then drops out of sight, invisible, for the next rise).
const HIDDEN_STATE = { opacity: 0, y: 64, scale: 0.94 };
const SHOWN_STATE = { opacity: 1, y: 0, scale: 1 };
const EXIT_TRANSITION = {
  opacity: { duration: STUDIO_EXIT_MS / 1000, ease: 'easeIn' as const },
  scale: { duration: STUDIO_EXIT_MS / 1000, ease: 'easeIn' as const },
  y: { delay: STUDIO_EXIT_MS / 1000, duration: 0 },
};

const ceilToPixel = (points: number) => Math.ceil(points * 3 - 1e-9) / 3;

// iOS draws a 15pt line's emoji 1.25× the text size (16.7pt of ink) on a
// 21pt advance, 1pt further right and 1.67pt lower than the browser's emoji
// font puts the same glyph (measured).
const EMOJI_ADVANCE = 21;
const EMOJI_FONT_SIZE = 18.75;
const EMOJI_OFFSET = '1px 1.667px';

/** One line of text, its emoji drawn at the iOS size and advance. */
function IosLine({ text }: { text: string }) {
  return splitIosEmoji(text).map((run, i) =>
    run.emoji ? (
      <span
        key={i}
        className="inline-block text-center"
        // `lineHeight: 0`: the bigger glyph must not grow the line box.
        style={{
          width: EMOJI_ADVANCE,
          fontSize: EMOJI_FONT_SIZE,
          lineHeight: 0,
          translate: EMOJI_OFFSET,
        }}
      >
        {run.text}
      </span>
    ) : (
      run.text
    )
  );
}

/**
 * A lone notification on the iOS 26 lock screen, drawn in points over a
 * 402×874pt screen scaled to `screenWidth` and anchored to the bottom (the
 * mockup's screen is a hair shorter than an iPhone 17's). Title and body are
 * cut and wrapped exactly as the phone does; empty fields show the
 * placeholders, laid out the same way.
 */
export const IosLockScreenNotification = memo(
  function IosLockScreenNotification({
    title,
    body,
    titlePlaceholder,
    bodyPlaceholder,
    timeLabel,
    iconUrl,
    screenWidth,
    visible,
    reduceMotion,
    onPress,
    pressLabel,
  }: {
    title: string;
    body: string;
    titlePlaceholder: string;
    bodyPlaceholder: string;
    timeLabel: string;
    iconUrl: string | null | undefined;
    screenWidth: number;
    visible: boolean;
    reduceMotion: boolean;
    /** Makes the card a button (e.g. "Editar mensagem" in an editor). */
    onPress?: () => void;
    pressLabel?: string;
  }) {
    const s = IOS_LOCK_SCREEN;
    const titleEmpty = title.trim() === '';
    const bodyEmpty = body.trim() === '';
    // The layout measures every line; only redo it when the text changes.
    const titleText = useMemo(
      () =>
        layoutIosNotificationTitle(
          titleEmpty ? titlePlaceholder : title,
          timeLabel
        ),
      [titleEmpty, titlePlaceholder, title, timeLabel]
    );
    const bodyLines = useMemo(
      () => layoutIosNotificationBody(bodyEmpty ? bodyPlaceholder : body),
      [bodyEmpty, bodyPlaceholder, body]
    );

    return (
      <div
        className="pointer-events-none absolute bottom-0 left-0 origin-bottom-left"
        style={{
          width: s.screenWidth,
          height: s.screenHeight,
          transform: `scale(${screenWidth / s.screenWidth})`,
          fontFamily: FONT_FAMILY,
          fontSynthesis: 'none',
        }}
      >
        <motion.div
          data-visible={visible || undefined}
          initial={false}
          animate={visible ? SHOWN_STATE : HIDDEN_STATE}
          transition={
            reduceMotion
              ? { duration: 0 }
              : visible
                ? NOTIFICATION_ENTRANCE_TRANSITION
                : EXIT_TRANSITION
          }
          className="absolute overflow-hidden bg-white/20 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.28)] backdrop-blur-[12px] backdrop-saturate-150"
          style={{
            left: s.cardSideInset,
            right: s.cardSideInset,
            bottom: s.cardBottomInset,
            height: iosNotificationCardHeight(bodyLines.length),
            borderRadius: s.cardRadius,
          }}
        >
          {onPress && (
            <button
              type="button"
              aria-label={pressLabel}
              onClick={onPress}
              className="pointer-events-auto absolute inset-0 z-10 cursor-pointer rounded-[inherit] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70"
            />
          )}
          <AppIcon
            iconUrl={iconUrl}
            className="absolute top-1/2 -translate-y-1/2 rounded-[22.37%]"
            style={{ left: s.iconInset, width: s.iconSize, height: s.iconSize }}
          />
          {/* Every line is exactly one line height: an emoji's taller font
            must not push the next line down (iOS keeps the 18pt rhythm). */}
          <div
            aria-live="polite"
            className="absolute whitespace-pre antialiased [&_p]:overflow-visible"
            style={{
              left: s.textInset,
              top: s.titleBaseline - LINE_BOX_BASELINE,
              width: s.textWidth,
              lineHeight: `${s.lineHeight}px`,
            }}
          >
            <div
              className="flex items-baseline"
              style={{ height: s.lineHeight }}
            >
              <p
                className={titleEmpty ? 'opacity-60' : undefined}
                style={{ fontSize: 15, fontWeight: SEMIBOLD }}
                data-placeholder={titleEmpty || undefined}
              >
                <IosLine text={titleText} />
              </p>
              <span
                className="ml-auto text-white/35 mix-blend-plus-lighter"
                style={{
                  fontSize: 13,
                  width: ceilToPixel(measureIosText(timeLabel, 'time')),
                }}
              >
                {timeLabel}
              </span>
            </div>
            <div
              className={bodyEmpty ? 'opacity-60' : undefined}
              style={{ fontSize: 15 }}
              data-placeholder={bodyEmpty || undefined}
            >
              {bodyLines.map((line, i) => (
                <p key={i} style={{ height: s.lineHeight }}>
                  <IosLine text={line} />
                </p>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }
);
